import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';

import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { Store, provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { provideTransloco } from '@ngneat/transloco';

import { ApiModule, Configuration, ConfigurationParameters } from '@digitalforest/ngx-oak-api';

import { environment } from '../environments/environment';
import { LanguageDirectionService } from '../services/language-direction.service';
import { TokenExpirationService } from '../services/token-expiration.service';
import { TokenService } from '../services/token.service';
import { AppRoutingModule } from './modules/app-routing.module';
import { AdministratorEffects } from './store/administrator.effects';
import { administratorFeature } from './store/administrator.feature';
import { ApiKeyInfoEffects } from './store/api-key-info.effects';
import { apiKeyInfoFeature } from './store/api-key-info.feature';
import { ScopeEffects } from './store/scope.effects';
import { scopeFeature } from './store/scope.feature';
import { TokenActions } from './store/token.actions';
import { TokenEffects } from './store/token.effects';
import { tokenFeature } from './store/token.feature';
import CustomPreset from './theme/custom-preset';
import { TranslocoHttpLoader } from './transloco-loader.module';

import { JWT_OPTIONS, JwtConfig, JwtModule } from '@auth0/angular-jwt';
import * as Sentry from '@sentry/angular';

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.API_BASE_PATH,
  };

  return new Configuration(params);
}

export function jwtOptionsFactory(tokenService: TokenService): JwtConfig {
  return {
    tokenGetter: () => {
      return tokenService.getToken();
    },
    allowedDomains: environment.ALLOWED_DOMAINS,
    disallowedRoutes: [],
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: CustomPreset,
        options: {
          darkModeSelector: '.dark-mode', // Use the class selector to properly apply dark mode
          cssLayer: {
            name: 'primeng',
            order: 'primeng, theme',
          },
        },
      },
    }),
    importProvidersFrom(
      ApiModule.forRoot(apiConfigFactory),
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [TokenService],
        },
      }),
      AppRoutingModule,
    ),
    TokenService,
    MessageService,
    LanguageDirectionService,
    provideTransloco({
      config: {
        availableLangs: ['en', 'en-GB', 'ca', 'de', 'es', 'fr', 'it', 'pl', 'pt', 'ar'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    provideStore(),
    provideState(tokenFeature),
    provideState(administratorFeature),
    provideState(scopeFeature),
    provideState(apiKeyInfoFeature),
    provideEffects([TokenEffects, AdministratorEffects, ScopeEffects, ApiKeyInfoEffects]),
    provideRouterStore(),
    !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],
    provideAppInitializer(() => {
      const store = inject(Store);
      inject(TokenExpirationService);
      inject(LanguageDirectionService); // Initialize language direction service

      // Check localStorage directly for debugging
      const localToken = localStorage.getItem('authentication');
      console.log(`[APP_INITIALIZER] Direct localStorage check: ${localToken ? 'token exists' : 'no token'}`);

      // Dispatch token loading action
      console.log('[APP_INITIALIZER] Dispatching TokenActions.loadToken()');
      store.dispatch(TokenActions.loadToken());

      console.log('[APP_INITIALIZER] TokenExpirationService is available but inactive until auth success');

      // Return resolved promise to complete initialization
      return Promise.resolve(true);
    }),
  ],
};
